import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";

// Metadata fields: created_at, updated_at, last_contacted_at, created_by, updated_by
//
// Declaration order is preserved from the pre-refactor file so the mobile
// "Metadata" detail section renders fields in the same order. Saved-view
// column order is supplied explicitly in `contact-saved-views.ts` because it
// differs from this declaration order (views render last activity before
// created date).
export const metadataContactFields = defineFieldConfigMap({
  // FIELD: created_at
  created_at: {
    type: "datetime",
    label: "Created",
    // DB-managed (default now()). Display-only — never sent on write.
    excludeFromApi: true,
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: false, // Auto-generated timestamp
    width: 160,
    minWidth: 120,
    maxWidth: 220,
    icon: "Calendar",
    groupable: true,
    groupingOptions: {
      dateGranularity: "month",
    },
    datetimePresets: {
      enabled: true,
      presets: [
        "today",
        "yesterday",
        "last7days",
        "last30days",
        "thismonth",
        "lastmonth",
        "thisyear",
        "lastyear",
      ],
      showCustomRange: true,
    },
    // Internal — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "metadata",
    graphqlSelector: "created_at",
    savedViewMembership: [
      "all-contacts",
      "active-contacts",
      "leads",
      "prospects",
      "no-tickets-this-month",
      "with-tickets-this-month",
      "no-tickets-last-month",
      "with-tickets-last-month",
    ],
  },
  // FIELD: updated_at
  updated_at: {
    type: "datetime",
    label: "Updated",
    // DB-managed (default now()). Display-only — never sent on write.
    excludeFromApi: true,
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: false, // Auto-generated timestamp
    width: 160,
    minWidth: 120,
    maxWidth: 220,
    icon: "Calendar",
    groupable: true,
    groupingOptions: {
      dateGranularity: "month",
    },
    datetimePresets: {
      enabled: true,
      presets: [
        "today",
        "yesterday",
        "last7days",
        "last30days",
        "thismonth",
        "lastmonth",
        "thisyear",
        "lastyear",
      ],
      showCustomRange: true,
    },
    // Internal — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "metadata",
    graphqlSelector: "updated_at",
  },
  // FIELD: last_contacted_at
  last_contacted_at: {
    type: "datetime",
    label: "Last activity",
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: false,
    isReadOnlyDerived: true,
    readOnlyReason: "Derived from the latest communication for this contact (max communications.occurred_at).",
    dataLineage: {
      kind: "trigger-populated",
      sourceTable: "communications",
      sourceField: "occurred_at",
      notes: "Per-contact max(occurred_at) via DB trigger; see migration engagement_last_activity_from_communications.",
    },
    width: 168,
    minWidth: 120,
    maxWidth: 220,
    icon: "MessageSquare",
    groupable: true,
    groupingOptions: {
      dateGranularity: "day",
    },
    datetimePresets: {
      enabled: true,
      presets: [
        "never",
        "today",
        "yesterday",
        "last7days",
        "last14days",
        "last30days",
        "thismonth",
        "lastmonth",
      ],
      showCustomRange: true,
    },
    engagementStaleness: true,
    // Internal — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "metadata",
    graphqlSelector: "last_contacted_at",
    savedViewMembership: [
      "all-contacts",
      "active-contacts",
      "customers",
      "leads",
      "prospects",
      "no-tickets-this-month",
      "with-tickets-this-month",
      "no-tickets-last-month",
      "with-tickets-last-month",
      "needs-follow-up",
      "contacts-with-contract",
      "contacts-without-contract",
      "cold-90-days",
      "contacts-with-active-deals",
      "contacts-without-active-deals",
    ],
  },
  // FIELD: last_follow_up_task_created_at
  last_follow_up_task_created_at: {
    type: "datetime",
    label: "Last follow-up task",
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: false,
    isReadOnlyDerived: true,
    readOnlyReason:
      "Set when the entity follow-up cron creates an automated follow-up task for this contact.",
    dataLineage: {
      kind: "derived",
      sourceTable: "tasks",
      notes: "Updated by entity-follow-up-tasks cron (lib/cron/entity-follow-up/create-task.ts).",
    },
    width: 168,
    minWidth: 120,
    maxWidth: 220,
    icon: "Clock",
    groupable: true,
    groupingOptions: {
      dateGranularity: "day",
    },
    datetimePresets: {
      enabled: true,
      presets: [
        "never",
        "today",
        "yesterday",
        "last7days",
        "last14days",
        "last30days",
        "thismonth",
        "lastmonth",
      ],
      showCustomRange: true,
    },
    engagementStaleness: true,
    // Internal — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "metadata",
    graphqlSelector: "last_follow_up_task_created_at",
    savedViewMembership: ["needs-follow-up"],
  },
  /**
   * FILTER: last_follow_up_days_ago_min
   * Virtual filter id (not a DB column). Saved views pass `filter_last_follow_up_days_ago_min`;
   * filter-conversion strips the `filter_` prefix and routes here via `toGraphQLFilter`.
   */
  last_follow_up_days_ago_min: {
    type: "number",
    label: "Follow-up stale after (days)",
    filterable: true,
    hideable: true,
    editable: false,
    excludeFromApi: true,
    // Virtual FILTER id only (not a real contacts column). formOnly keeps it out
    // of the rendered table columns so it never shows as an empty "column that
    // isn't a column"; filter resolution reads `toGraphQLFilter` by field id.
    formOnly: true,
    toGraphQLFilter: (value, context) => {
      const minDays = typeof value === "number" ? value : Number(value);
      if (!Number.isFinite(minDays) || minDays < 0) return null;
      const cutoff = new Date(context.now);
      cutoff.setUTCDate(cutoff.getUTCDate() - minDays);
      return {
        or: [
          { last_follow_up_task_created_at: { is: "NULL" } },
          { last_follow_up_task_created_at: { lte: cutoff.toISOString() } },
        ],
      };
    },
  },
  // FIELD: created_by
  created_by: {
    type: "text",
    label: "Created By",
    isForeignKey: true,
    foreignKey: {
      referencedTable: "app_users",
      referencedColumn: "id",
      constraintName: "contacts_created_by_fkey",
      onDelete: "SET NULL",
      onUpdate: "NO ACTION",
      relationKind: "many-to-one",
    },
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    // User picker filter — first option resolves to the current logged-in user
    // so staff can quickly filter to contacts they created.
    filterType: "user-select",
    width: 140,
    minWidth: 120,
    maxWidth: 180,
    placeholder: "User ID",
    icon: "User",
    excludeFromApi: true,
    // Internal — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "metadata",
    // graphqlSelector omitted: relation expands to nested `app_users { ... }`
    // selection — not a simple scalar mapping.
  },
  // FIELD: updated_by
  updated_by: {
    type: "text",
    label: "Updated By",
    isForeignKey: true,
    foreignKey: {
      referencedTable: "app_users",
      referencedColumn: "id",
      constraintName: "contacts_updated_by_fkey",
      onDelete: "SET NULL",
      onUpdate: "NO ACTION",
      relationKind: "many-to-one",
    },
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    filterType: "contains",
    width: 140,
    minWidth: 120,
    maxWidth: 180,
    placeholder: "User ID",
    icon: "User",
    excludeFromApi: true,
    // Internal — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "metadata",
    // graphqlSelector omitted: relation expands to nested `app_users { ... }`
    // selection — not a simple scalar mapping.
  },
});
